import type { ApiErrorInfo } from "@/lib/api";
import { categoryForReasonCode } from "@/i18n/error-categories";

/**
 * Reason codes and HTTP statuses that mean "you may not read this", as opposed to
 * "it failed" or "it is not there". Shared by every page that branches a failed
 * read into PermissionDeniedState / ErrorState / not-found (ALL-5736).
 */
const DENIED_REASON_CODES = new Set([
  "HTTP_401",
  "HTTP_403",
  "AUTH_INSUFFICIENT_SCOPE",
  "AUTH_INSUFFICIENT_PERMISSION",
  "AUTH_CONTEXT_INVALID",
  "AUTH_TENANT_MISMATCH",
  "AUTH_TRUST_DOMAIN_MISMATCH",
  "AUTH_POLICY_DENIED",
  "AUTH_ROLE_NOT_ALLOWED",
  "AUTH_RESOURCE_OWNERSHIP_MISMATCH",
  "AUTH_FORBIDDEN",
]);

export type FailureKind = "denied" | "not_found" | "error";

export function classifyFailure(error: { reasonCode?: string | null } | null | undefined, status?: number): FailureKind {
  const code = error?.reasonCode ?? "";
  if (status === 401 || status === 403 || DENIED_REASON_CODES.has(code)) return "denied";
  if (status === 404 || code === "HTTP_404" || code === "NOT_FOUND" || /not found/i.test(code)) return "not_found";
  return "error";
}

export function isDeniedFailure(error: ApiErrorInfo | null | undefined): boolean {
  return error ? classifyFailure(error) === "denied" : false;
}

/** The hooks that keep only a reason-code string pass it through here. */
export function failureFromReason(reason: string | ApiErrorInfo): ApiErrorInfo {
  return typeof reason === "string" ? { reasonCode: reason } : reason;
}

/**
 * ALL-5774 (founder, 2026-10-02: "investigate the error. and findout why I saw a raw json as
 * response. This shouldnt be the case across the application"): the plain-language sentence
 * for a failure, from the error-page category of its reason code (an `HTTP_<status>` code
 * maps by its status). For inline notices that used to print the raw reason code.
 */
export function plainErrorMessage(
  catalog: { categories: Record<string, { title: string; message: string }> },
  error: { reasonCode?: string | null } | null | undefined,
): string {
  const code = (error?.reasonCode ?? "").trim();
  // A fetch that never got a response: the service could not be reached.
  if (code === "NETWORK_ERROR" || /failed to fetch|network/i.test(code)) {
    return (catalog.categories.service_unavailable ?? catalog.categories.generic_4xx)?.message ?? "";
  }
  const status = /^HTTP_([45][0-9][0-9])$/.exec(code);
  const category = status
    ? categoryForReasonCode(undefined, Number(status[1]))
    : categoryForReasonCode(code || undefined);
  return (catalog.categories[category] ?? catalog.categories.generic_4xx)?.message ?? "";
}

/**
 * The admin fetch helpers in lib/api.ts report a failed read as the text `HTTP <status>`, or
 * `Network error` when no response came back (ALL-5780). This turns that text into the reason code
 * plainErrorMessage understands (`HTTP_<status>`, `NETWORK_ERROR`), so a page shows the category
 * sentence and keeps the code only as a detail. Anything else is passed through as a reason code.
 */
export function legacyErrorReason(text: string | null | undefined): string {
  const value = (text ?? "").trim();
  const status = /^HTTP\s+([1-5][0-9][0-9])$/.exec(value);
  if (status) return `HTTP_${status[1]}`;
  if (/^network error$/i.test(value) || /unable to connect/i.test(value)) return "NETWORK_ERROR";
  return value;
}
